import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, CheckSquare, Clock, AlertTriangle, Circle, X } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { sortPeopleWithSelf, displayName } from '../lib/people';
import { EntityFilterPicker, type FilterEntity, type FilterModes, DEFAULT_FILTER_MODES } from '../components/EntityFilterPicker';
import { EntityChip, type EntityType } from '../components/EntityChip';

interface LinkedEntity {
  entity_type: string;
  entity_id: string;
  name: string;
}

interface Task {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  tag_ids: string[];
  scope_ids: string[];
  linked_entities: LinkedEntity[];
  created_at: string;
}

interface Tag { id: string; name: string; color: string; }
interface Scope { id: string; name: string; color: string; }
interface Person { id: string; name: string; is_self?: boolean; }

const STATUS_OPTIONS = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'];

const STATUS_LABELS: Record<string, string> = {
  todo: 'Todo',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

const STATUS_TOGGLE_COLORS: Record<string, { active: string; icon: string }> = {
  todo: { active: 'bg-text-muted/15 border-text-muted text-text-muted', icon: 'text-text-muted' },
  in_progress: { active: 'bg-accent/15 border-accent text-accent', icon: 'text-accent' },
  blocked: { active: 'bg-danger/15 border-danger text-danger', icon: 'text-danger' },
  done: { active: 'bg-success/15 border-success text-success', icon: 'text-success' },
  cancelled: { active: 'bg-text-muted/15 border-text-muted text-text-muted', icon: 'text-text-muted' },
};

const DEFAULT_STATUS_FILTERS = new Set(['todo', 'in_progress', 'blocked']);

function loadStatusFilters(): Set<string> {
  try {
    const saved = localStorage.getItem('dossier-task-status-filters');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return new Set(parsed.filter((s: string) => STATUS_OPTIONS.includes(s as typeof STATUS_OPTIONS[number])));
    }
  } catch { /* ignore */ }
  return new Set(DEFAULT_STATUS_FILTERS);
}

function saveStatusFilters(filters: Set<string>) {
  localStorage.setItem('dossier-task-status-filters', JSON.stringify(Array.from(filters)));
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={14} className="text-text-muted" />,
  in_progress: <Clock size={14} className="text-accent" />,
  blocked: <AlertTriangle size={14} className="text-danger" />,
  done: <CheckSquare size={14} className="text-success" />,
  cancelled: <X size={14} className="text-text-muted" />,
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-danger',
  high: 'text-warning',
  medium: 'text-text-secondary',
  low: 'text-text-muted',
};

export function TasksPage() {
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Set<string>>(loadStatusFilters);
  const [filterPriority, setFilterPriority] = useState('');
  const [filterEntities, setFilterEntities] = useState<FilterEntity[]>([]);
  const [filterModes, setFilterModes] = useState<FilterModes>({});

  function toggleStatusFilter(status: string) {
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      saveStatusFilters(next);
      return next;
    });
  }

  async function loadData() {
    try {
      const params = new URLSearchParams();
      if (filterPriority) params.set('priority', filterPriority);

      // Group filterEntities by type and build plural query params
      const byType = new Map<string, string[]>();
      for (const fe of filterEntities) {
        const ids = byType.get(fe.entity_type) ?? [];
        ids.push(fe.entity_id);
        byType.set(fe.entity_type, ids);
      }
      const tagIds = byType.get('tag');
      if (tagIds?.length) params.set('tag_ids', tagIds.join(','));
      const scopeIds = byType.get('scope');
      if (scopeIds?.length) params.set('scope_ids', scopeIds.join(','));
      const assigneeIds = byType.get('person');
      if (assigneeIds?.length) params.set('assignee_ids', assigneeIds.join(','));
      const seriesIds = byType.get('meeting_series');
      if (seriesIds?.length) params.set('series_ids', seriesIds.join(','));
      const meetingIds = byType.get('meeting_instance');
      if (meetingIds?.length) params.set('source_meeting_ids', meetingIds.join(','));

      // AND/OR mode params — only send when non-default
      if (tagIds && tagIds.length > 1) {
        const tagMode = filterModes.tag ?? DEFAULT_FILTER_MODES.tag ?? 'or';
        if (tagMode !== 'and') params.set('tag_mode', tagMode);
      }
      if (scopeIds && scopeIds.length > 1) {
        const scopeMode = filterModes.scope ?? DEFAULT_FILTER_MODES.scope ?? 'or';
        if (scopeMode !== 'or') params.set('scope_mode', scopeMode);
      }

      params.set('sort_by', 'created_at');
      params.set('sort_dir', 'desc');

      const [t, tags, scp, ppl] = await Promise.all([
        apiGet<Task[]>(`/tasks?${params}`),
        apiGet<Tag[]>('/tags'),
        apiGet<Scope[]>('/scopes'),
        apiGet<Person[]>('/people'),
      ]);
      setTasks(t);
      setTags(tags);
      setScopes(scp);
      setPeople(ppl);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [filterPriority, filterEntities, filterModes]);

  async function handleCreate(title: string, priority: string, assigneeId: string, dueDate: string) {
    try {
      await apiPost('/tasks', {
        title,
        priority: priority || 'medium',
        assignee_id: assigneeId || null,
        due_date: dueDate || null,
      });
      setShowCreate(false);
      toast.success('Task created');
      loadData();
    } catch {
      toast.error('Failed to create task');
    }
  }

  async function handleStatusChange(id: string, status: string) {
    await apiPatch(`/tasks/${id}`, { status });
    loadData();
  }

  async function handleDeleteTask(id: string) {
    if (!window.confirm('Delete this task?')) return;
    try {
      await apiDelete(`/tasks/${id}`);
      toast.success('Task deleted');
      loadData();
    } catch {
      toast.error('Failed to delete task');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));
  const scopeMap = new Map(scopes.map(s => [s.id, s]));

  // Count tasks per status and apply client-side status filter
  const statusCounts: Record<string, number> = {};
  for (const t of tasks) {
    statusCounts[t.status] = (statusCounts[t.status] || 0) + 1;
  }
  const filteredTasks = statusFilters.size === 0
    ? tasks
    : tasks.filter(t => statusFilters.has(t.status));
  const hasActiveFilters = statusFilters.size !== STATUS_OPTIONS.length || filterPriority || filterEntities.length > 0 || Object.keys(filterModes).length > 0;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Tasks</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Filter size={14} className="text-text-muted flex-shrink-0" />
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map(status => {
            const isActive = statusFilters.has(status);
            const count = statusCounts[status] || 0;
            const colors = STATUS_TOGGLE_COLORS[status];
            return (
              <button
                key={status}
                onClick={() => toggleStatusFilter(status)}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  isActive
                    ? colors.active
                    : 'border-border text-text-muted/50 hover:text-text-muted hover:border-border-hover'
                }`}
              >
                {STATUS_LABELS[status]}
                {count > 0 && (
                  <span className={`text-[10px] ${isActive ? 'opacity-80' : 'opacity-50'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-2 py-1 bg-bg-input border border-border rounded text-sm text-text-primary"
        >
          <option value="">All Priorities</option>
          {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <EntityFilterPicker
          selected={filterEntities}
          onChange={setFilterEntities}
          placeholder="Filter by tags, projects, people, meetings..."
          filterModes={filterModes}
          onFilterModesChange={setFilterModes}
        />
        {hasActiveFilters && (
          <button onClick={() => { setStatusFilters(new Set(DEFAULT_STATUS_FILTERS)); saveStatusFilters(new Set(DEFAULT_STATUS_FILTERS)); setFilterPriority(''); setFilterEntities([]); setFilterModes({}); }} className="text-xs text-text-muted hover:text-text-primary">
            Reset filters
          </button>
        )}
      </div>

      {/* Task List */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <CheckSquare size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">{tasks.length === 0 ? 'No tasks found' : 'No tasks match current filters'}</p>
          <p className="text-sm">{tasks.length === 0 ? 'Create a task to get started.' : 'Try adjusting your status or priority filters.'}</p>
        </div>
      ) : (
        <div className="space-y-1">
          {filteredTasks.map((task) => (
            <div key={task.id} className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group">
              <button
                onClick={() => handleStatusChange(task.id, task.status === 'done' ? 'todo' : 'done')}
                className="flex-shrink-0"
                title={task.status}
              >
                {STATUS_ICONS[task.status] ?? <Circle size={14} />}
              </button>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/tasks/${task.id}`}
                  className={`text-sm hover:text-accent transition-colors ${task.status === 'done' ? 'line-through text-text-muted' : 'text-text-primary'}`}
                >
                  {task.title}
                </Link>
                <div className="flex items-center gap-2 mt-0.5">
                  {task.assignee_name && (
                    <span className="text-xs text-text-muted">@{task.assignee_name}</span>
                  )}
                  {task.tag_ids.map(tid => {
                    const tag = tagMap.get(tid);
                    return tag ? (
                      <span key={`t-${tid}`} className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                        {tag.name}
                      </span>
                    ) : null;
                  })}
                  {task.scope_ids.map(sid => {
                    const scope = scopeMap.get(sid);
                    return scope ? (
                      <span key={`s-${sid}`} className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: scope.color + '20', color: scope.color }}>
                        {scope.name}
                      </span>
                    ) : null;
                  })}
                  {task.linked_entities?.map(le => (
                    <EntityChip key={`le-${le.entity_type}-${le.entity_id}`} entityType={le.entity_type as EntityType} name={le.name} />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {task.due_date && (
                  <span className={`text-xs ${isOverdue(task.due_date) && task.status !== 'done' ? 'text-danger' : 'text-text-muted'}`}>
                    {formatShortDate(task.due_date)}
                  </span>
                )}
                <span className={`text-xs capitalize ${PRIORITY_COLORS[task.priority]}`}>
                  {task.priority}
                </span>
                <button
                  onClick={() => handleDeleteTask(task.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTaskModal people={people} onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
    </div>
  );
}

function CreateTaskModal({
  people,
  onSave,
  onClose,
}: {
  people: Person[];
  onSave: (title: string, priority: string, assigneeId: string, dueDate: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New Task</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} autoFocus placeholder="What needs to be done?"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm">
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Assignee</label>
            <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm">
              <option value="">Unassigned</option>
              {sortPeopleWithSelf(people).map(p => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button onClick={() => title.trim() && onSave(title.trim(), priority, assigneeId, dueDate)} disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50">Create</button>
        </div>
      </div>
    </div>
  );
}

function formatShortDate(d: string): string {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOverdue(d: string): boolean {
  return new Date(d + 'T23:59:59') < new Date();
}
